<template>
  <div class="flex h-full flex-col">
    <div class="flex flex-col items-start gap-4 self-stretch">
      <div class="flex items-center gap-4 self-stretch">
        <UIButton id="backButton" class="pl-0" :quaternary="true" :text="false" @click="onBack">
          <ArrowLeftIcon class="h-5 w-5"></ArrowLeftIcon>
          {{ t("common.back") }}
        </UIButton>
        <UITextMdMedium>{{ t("common.payment.product.productSettings") }}</UITextMdMedium>
      </div>
      <div class="flex flex-1 flex-col items-start justify-between gap-3 self-stretch">
        <div class="flex flex-col items-start justify-between gap-3 self-stretch">
          <div class="flex space-x-3">
            <UITextSmMedium class="text-gray-700">{{
              t("common.payment.product.selectProduct")
            }}</UITextSmMedium>
          </div>
          <UISelect
            id="select-id"
            :value="product?.name"
            filterable
            :options="productList"
            :placeholder="t('common.payment.product.productSelectPlaceholder')"
            :reset-menu-on-options-change="false"
            :loading="isLoading"
            :disabled="!!currentProduct"
            clearable
            remote
            size="medium"
            @scroll="handleScroll"
            @update:value="updateProduct"
            @search="handleSearch"
          />
          <UITextSmRegular
            >{{
              currentProduct
                ? t("common.payment.product.editThisProduct")
                : t("common.payment.product.createProduct")
            }}
            <a class="cursor-pointer" @click="onProductEdit">{{
              t("common.payment.product.clickHere")
            }}</a>
          </UITextSmRegular>
        </div>
        <section class="flex flex-col gap-2">
          <UITextSmMedium class="text-gray-700">{{
            t("common.payment.product.chooseProductType")
          }}</UITextSmMedium>
          <div class="flex items-center gap-2">
            <UIRadio
              :id="PRODUCT_TYPE.MAIN_PRODUCT.value"
              :key="PRODUCT_TYPE.MAIN_PRODUCT.value"
              class="flex items-center"
              :disabled="!!currentProduct"
              :value="PRODUCT_TYPE.MAIN_PRODUCT.value"
              :checked="product?.type === PRODUCT_TYPE.MAIN_PRODUCT.value"
              @change="updateProductType"
            >
              <UITextSmRegular class="text-gray-700">{{
                PRODUCT_TYPE.MAIN_PRODUCT.label
              }}</UITextSmRegular>
            </UIRadio>
            <UIRadio
              :id="PRODUCT_TYPE.BUMP_PRODUCT.value"
              :key="PRODUCT_TYPE.BUMP_PRODUCT.value"
              :disabled="!!currentProduct"
              class="flex items-center"
              :value="PRODUCT_TYPE.BUMP_PRODUCT.value"
              :checked="product?.type === PRODUCT_TYPE.BUMP_PRODUCT.value"
              @change="updateProductType"
            >
              <UITextSmRegular class="flex items-center gap-2 text-gray-700"
                >{{ PRODUCT_TYPE.BUMP_PRODUCT.label }}
                <UITooltip trigger="hover" placement="top" width="300">
                  <template #trigger>
                    <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                  </template>
                  {{ t("common.payment.bumProduct.bumpProductTooltip") }}
                </UITooltip></UITextSmRegular
              >
            </UIRadio>
          </div>
        </section>
        <BumProductSettings
          v-if="product?.type === PRODUCT_TYPE.BUMP_PRODUCT.value"
          :product="product"
          :is-edit="!!currentProduct"
          @update:bum-product="updateBumProduct"
          @validate:bum-product-settings="validateBumProductSettings"
        />
        <div v-else class="flex items-center gap-4 self-stretch">
          <div class="flex flex-col items-start justify-between gap-2 self-stretch">
            <div class="flex space-x-3">
              <UITextSmMedium>{{ t("common.payment.product.show") }}</UITextSmMedium>
            </div>
            <UICheckbox
              id="showDescription"
              :checked="mainProductSettings?.showDescription"
              @update:checked="onDescriptionUpdate"
            >
              <UITextSmMedium>{{
                t("common.payment.product.description")
              }}</UITextSmMedium></UICheckbox
            >
            <UICheckbox
              id="showImage"
              :checked="mainProductSettings?.showImage"
              @update:checked="onImageUpdate"
            >
              <UITextSmMedium>{{ t("common.payment.product.image") }}</UITextSmMedium></UICheckbox
            >
            <UICheckbox
              id="showQuantity"
              :checked="mainProductSettings?.showQuantity"
              @update:checked="onQuantityUpdate"
            >
              <UITextSmMedium>{{
                t("common.payment.product.quantity")
              }}</UITextSmMedium></UICheckbox
            >
          </div>
        </div>
      </div>
    </div>

    <div class="sticky bottom-0 mt-auto bg-white">
      <div class="flex w-full flex-col gap-2">
        <div class="border-button">
          <div class="flex w-full items-center justify-between gap-2 px-2">
            <UIButton id="productCancel" class="flex-1" size="medium" @click="onBack">
              {{ t("common.cancel") }}
            </UIButton>
            <UIButton
              id="addProduct"
              type="primary"
              class="flex-1"
              size="medium"
              @click="addProduct"
            >
              {{ t("common.payment.product.addProduct") }}
            </UIButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useNotify } from "@/composition/notification";
import config from "@/config";
import { PaymentServices } from "@/service/PaymentServices";
import { useAppStore } from "@/store/app";
import { BumProduct } from "@/types/product";
import { PRODUCT_TYPE } from "@/util/productConstants";
import { ArrowLeftIcon, InfoCircleIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UICheckbox,
  UIForm,
  UIRadio,
  UISelect,
  UITextMdMedium,
  UITextSmMedium,
  UITextSmRegular,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import BumProductSettings from "./BumProductSettings.vue";

const { sendNotification } = useNotify();
const { t } = useI18n();
const store = useAppStore();
const props = defineProps({
  currentProduct: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(["onBack"]);
const product = ref(props.currentProduct);
const skip = ref(0);
const productList = ref(store.productList);
const isLoading = ref(false);
const totalProduct = ref(0);
const bumProductSettingsFormRef = ref<InstanceType<typeof UIForm>>();
const mainProductSettings = ref({
  showDescription: props.currentProduct?.showDescription ?? true,
  showImage: props.currentProduct?.showImage ?? true,
  showQuantity: props.currentProduct?.showQuantity ?? true,
});
const handleScroll = async (e) => {
  isLoading.value = true;
  if (hasNextPage.value) {
    const currentTarget = e.currentTarget as HTMLElement;
    if (currentTarget.scrollTop + currentTarget.offsetHeight + 20 >= currentTarget.scrollHeight) {
      skip.value += 10;
      await handlePaginationAPICall("", skip.value, 10);
    }
  }

  isLoading.value = false;
};

const handleSearch = async (search) => {
  await handlePaginationAPICall(search, 0, 10, "replace");
};

const handlePaginationAPICall = async (search = "", skip, limit, type = "append") => {
  const response = await PaymentServices.getProductList({
    locationId: store.locationId,
    search,
    skip,
    limit,
  });
  if (type === "append") {
    response.data.products.forEach((el) => {
      const obj = {
        productId: el._id,
        amount: el.prices[0].amount,
        currency: el.prices[0].currency,
        showDescription: true,
        showImage: true,
        showQuantity: true,
        ...el,
      };
      productList.value.push({ label: obj.name, value: obj });
    });
  } else {
    productList.value = response.data.products.map((el) => {
      const obj = {
        productId: el._id,
        amount: el.prices[0].amount,
        currency: el.prices[0].currency,
        showDescription: true,
        showImage: true,
        showQuantity: true,
        ...el,
      };
      return { label: obj.name, value: obj };
    });
  }
};

const onProductEdit = () => {
  if (props.currentProduct) {
    window
      .open(
        `https://${store.company.domain ? store.company.domain : config.GHL_DOMAIN}/v2/location/${
          store.locationId
        }/payments/products/edit/${product.value.productId}`,
        "_blank"
      )
      .focus();
  } else {
    window
      .open(
        `https://${store.company.domain ? store.company.domain : config.GHL_DOMAIN}/v2/location/${
          store.locationId
        }/payments/products`,
        "_blank"
      )
      .focus();
  }
};
const hasNextPage = computed(() => {
  return skip.value < totalProduct.value;
});

onMounted(async () => {
  totalProduct.value = store.totalProduct;
});

const updateProduct = async (value) => {
  product.value = value;
  product.value.type = PRODUCT_TYPE.MAIN_PRODUCT.value;
  product.value.priceId = value.prices[0]._id;
  store.anyUnsavedChanges = true;

  if (!value) {
    await handlePaginationAPICall("", 0, 10);
  }
};

const validateBumProductSettings = (e: InstanceType<typeof UIForm>) => {
  bumProductSettingsFormRef.value = e;
};

const addBumProduct = () => {
  const productAlreadyAdded = store.bumProductList.filter((item) => {
    return item.priceId === bumpProduct.value.priceId;
  });
  if (productAlreadyAdded.length > 0) {
    sendNotification({
      title: t("common.payment.error.productAlreadyAdded"),
      type: "error",
    });
    return;
  }

  if (store.addedProductList.length < 1) {
    sendNotification({
      title: t("common.payment.error.addMainProductFirst"),
      type: "error",
    });
    return;
  }

  if (!props.currentProduct) {
    const priceProduct = product.value.prices.find(
      (price) => price._id === bumpProduct.value.priceId
    );
    if (!priceProduct) {
      sendNotification({
        title: t("common.payment.error.priceNotFoundForBumpProduct"),
        type: "error",
      });
      return;
    }
    const newBumProduct = {
      ...product.value,
      ...bumpProduct.value,
      amount: priceProduct.amount,
      type: PRODUCT_TYPE.BUMP_PRODUCT.value,
      currency: priceProduct.currency,
      compareAtPrice: priceProduct.compareAtPrice,
    };
    store.bumProductList.push(newBumProduct);
    if (store.bumProductList.length === 1) {
      store.payment.bumpSettings.enableBump = true;
    }
    store.anyUnsavedChanges = true;
  }
  emit("onBack");
};

const updateBumProductSettings = () => {
  store.bumProductList.forEach((storeProduct) => {
    if (storeProduct.priceId === bumpProduct.value.priceId) {
      storeProduct.headline = bumpProduct.value.headline;
      storeProduct.otoHeadline = bumpProduct.value.otoHeadline;
      storeProduct.otoText = bumpProduct.value.otoText;
      storeProduct.enableImage = bumpProduct.value.enableImage;
      storeProduct.uploadedImage = bumpProduct.value.uploadedImage;
      storeProduct.amount = bumpProduct.value.amount;
      storeProduct.currency = bumpProduct.value.currency;
      storeProduct.type = PRODUCT_TYPE.BUMP_PRODUCT.value;
    }
  });
  emit("onBack");
};

const addProduct = async () => {
  if (bumProductSettingsFormRef.value && product.value.type === PRODUCT_TYPE.BUMP_PRODUCT.value) {
    const isValid = await bumProductSettingsFormRef.value.getForm().validate();
    if (!isValid) {
      return;
    }
  }
  if (!props.currentProduct && product.value.type === PRODUCT_TYPE.BUMP_PRODUCT.value) {
    addBumProduct();
    return;
  } else if (props.currentProduct && product.value.type === PRODUCT_TYPE.BUMP_PRODUCT.value) {
    updateBumProductSettings();
    return;
  }
  const productAlreadyAdded = store.addedProductList.filter((item) => {
    return item.productId === product.value.productId;
  });
  if (!props.currentProduct && productAlreadyAdded.length > 0) {
    sendNotification({
      title: "Product already added",
      type: "error",
    });
  }
  if (!props.currentProduct && productAlreadyAdded.length <= 0) {
    if (store.addedProductList) {
      store.addedProductList.push(product.value);
    } else {
      store.addedProductList = product.value as Array<any>;
    }
  }
  if (props.currentProduct && productAlreadyAdded.length === 1) {
    store.addedProductList.forEach((storeProduct) => {
      if (storeProduct.productId === productAlreadyAdded[0].productId) {
        storeProduct = {
          ...product.value,
          ...mainProductSettings.value,
        };
      }
    });
  }
  if (props.currentProduct) {
    store.addedProductList.forEach((storeProduct) => {
      if (storeProduct.productId === props.currentProduct.productId) {
        storeProduct.showDescription = mainProductSettings.value.showDescription;
        storeProduct.showImage = mainProductSettings.value.showImage;
        storeProduct.showQuantity = mainProductSettings.value.showQuantity;
      }
    });
  }
  store.anyUnsavedChanges = true;
  emit("onBack");
};

const onDescriptionUpdate = (isDescriptionEnabled: boolean) => {
  mainProductSettings.value.showDescription = isDescriptionEnabled;
  store.anyUnsavedChanges = true;
};

const onImageUpdate = (isImageEnabled: boolean) => {
  mainProductSettings.value.showImage = isImageEnabled;
  store.anyUnsavedChanges = true;
};
const onQuantityUpdate = (isQuantityEnabled: boolean) => {
  mainProductSettings.value.showQuantity = isQuantityEnabled;
  store.anyUnsavedChanges = true;
};

const updateProductType = (e: Event) => {
  product.value.type = (e.target as HTMLInputElement).value;
  store.anyUnsavedChanges = true;
};

const bumpProduct = ref<BumProduct>();
const updateBumProduct = (bumProduct: BumProduct) => {
  bumpProduct.value = bumProduct;
  store.anyUnsavedChanges = true;
};

const onBack = () => {
  emit("onBack");
};
</script>

<style scoped>
a {
  text-decoration: underline !important;
  color: rgb(13, 110, 253) !important;
}

.border-button {
  margin-left: -1rem;
  margin-right: -1rem;
  border-top: 1px solid rgb(229 231 235);
  border-bottom: none;
  padding-top: 1rem;
}
</style>
